
import {
  useEffect,
  useState,
  type FormEvent,
} from "react";
import {
  ArrowRight,
  CheckCircle2,
  Church,
  Circle,
  Eye,
  EyeOff,
  KeyRound,
} from "lucide-react";
import {
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { AuthLayout } from "@/components/auth/AuthLayout";
import { resetPassword } from "../api/authApi";

const passwordRequirements = [
  {
    label: "12–100 characters",
    test: (value: string) =>
      value.length >= 12 && value.length <= 100,
  },
  {
    label: "At least one uppercase letter",
    test: (value: string) =>
      [...value].some((character) =>
        character.toUpperCase() !==
          character.toLowerCase() &&
        character === character.toUpperCase(),
      ),
  },
  {
    label: "At least one lowercase letter",
    test: (value: string) =>
      [...value].some((character) =>
        character.toUpperCase() !==
          character.toLowerCase() &&
        character === character.toLowerCase(),
      ),
  },
  {
    label: "At least one number",
    test: (value: string) =>
      [...value].some((character) =>
        character >= "0" && character <= "9",
      ),
  },
  {
    label: "At least one special character",
    test: (value: string) =>
      !/^[A-Za-z0-9]+$/.test(value),
  },
  {
    label: "No leading or trailing spaces",
    test: (value: string) =>
      value.length > 0 && value === value.trim(),
  },
];

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] =
    useState("");

  const [showPassword, setShowPassword] =
    useState(false);

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] = useState(false);

  const [errorMessage, setErrorMessage] =
    useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [isComplete, setIsComplete] =
    useState(false);

  const requirementsMet =
    passwordRequirements.every((requirement) =>
      requirement.test(password),
    );

  const passwordsMatch =
    confirmPassword.length > 0 &&
    password === confirmPassword;

  const canSubmit =
    Boolean(token) &&
    requirementsMet &&
    passwordsMatch &&
    !isSubmitting;

  useEffect(() => {
    setErrorMessage(null);
  }, [password, confirmPassword]);

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    if (!token) {
      setErrorMessage(
        "This password reset link is invalid. Request a new password reset link.",
      );
      return;
    }

    if (!requirementsMet) {
      setErrorMessage(
        "Your password does not meet all the requirements below.",
      );
      return;
    }

    if (!passwordsMatch) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await resetPassword({
        token,
        new_password: password,
      });

      setIsComplete(true);

      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error: unknown) {
      const response = (
        error as {
          response?: {
            status?: number;
            data?: {
              detail?: string;
              message?: string;
            };
          };
        }
      )?.response;

      const detail = response?.data?.detail;

      if (
        typeof detail === "string" &&
        detail.length > 0
      ) {
        setErrorMessage(detail);
      } else if (response?.status === 422) {
        setErrorMessage(
          "Unable to update your password. Choose a new password that meets all the requirements and is different from your current password.",
        );
      } else if (response?.status === 400) {
        setErrorMessage(
          "This password reset link is invalid or has expired. Request a new password reset link.",
        );
      } else {
        setErrorMessage(
          "Unable to reset your password. Please try again.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthLayout
      variant="church"
      workspaceLabel="Church Workspace"
      brandIcon={Church}
      cardIcon={KeyRound}
      eyebrow="Password Reset"
      heroTitle="Create your new password."
      heroDescription="Choose a strong password to secure your MinistryIQ workspace."
      formTitle={
        isComplete
          ? "Password Updated"
          : "Choose a New Password"
      }
      formDescription={
        isComplete
          ? "Your password has been changed successfully."
          : "Enter a new password that meets the security requirements below."
      }
      footer="Password reset links expire after 30 minutes."
    >
      {isComplete ? (
        <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
          <div className="flex items-start gap-4">
            <CheckCircle2 className="mt-0.5 h-7 w-7 text-emerald-600" />

            <div>
              <p className="font-semibold text-emerald-900">
                Password successfully updated
              </p>

              <p className="mt-2 text-sm leading-6 text-emerald-800">
                Redirecting you back to sign in...
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {errorMessage && (
            <div
              role="alert"
              className="mt-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3"
            >
              <p className="text-sm font-medium text-rose-700">
                {errorMessage}
              </p>
            </div>
          )}

          {!token && (
            <div
              role="alert"
              className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
            >
              <p className="text-sm text-amber-800">
                Your password reset link is missing its
                security token. Request a new reset link.
              </p>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="mt-8 space-y-5"
          >
            <div>
              <PasswordField
                label="New Password"
                value={password}
                setValue={setPassword}
                visible={showPassword}
                setVisible={setShowPassword}
                autoComplete="new-password"
              />

              <div
                className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4"
                aria-label="Password requirements"
              >
                <p className="mb-3 text-sm font-semibold text-slate-700">
                  Password requirements
                </p>

                <ul className="space-y-2">
                  {passwordRequirements.map(
                    (requirement) => {
                      const met =
                        requirement.test(password);

                      return (
                        <li
                          key={requirement.label}
                          className="flex items-center gap-2 text-sm"
                        >
                          {met ? (
                            <CheckCircle2
                              aria-hidden="true"
                              className="h-4 w-4 shrink-0 text-emerald-600"
                            />
                          ) : (
                            <Circle
                              aria-hidden="true"
                              className="h-4 w-4 shrink-0 text-slate-400"
                            />
                          )}

                          <span
                            className={
                              met
                                ? "font-medium text-emerald-700"
                                : "text-slate-600"
                            }
                          >
                            {requirement.label}
                          </span>
                        </li>
                      );
                    },
                  )}
                </ul>
              </div>
            </div>

            <div>
              <PasswordField
                label="Confirm Password"
                value={confirmPassword}
                setValue={setConfirmPassword}
                visible={showConfirmPassword}
                setVisible={setShowConfirmPassword}
                autoComplete="new-password"
              />

              {confirmPassword.length > 0 && (
                <p
                  className={`mt-2 flex items-center gap-2 text-sm ${
                    passwordsMatch
                      ? "text-emerald-700"
                      : "text-rose-600"
                  }`}
                >
                  {passwordsMatch ? (
                    <>
                      <CheckCircle2
                        aria-hidden="true"
                        className="h-4 w-4"
                      />
                      Passwords match
                    </>
                  ) : (
                    "Passwords do not match."
                  )}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? (
                "Updating Password..."
              ) : (
                <>
                  Update Password
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            <Link
              to="/login"
              className="block text-center text-sm font-semibold text-slate-500 hover:text-indigo-600"
            >
              Return to Sign In
            </Link>
          </form>
        </>
      )}
    </AuthLayout>
  );
}

interface PasswordFieldProps {
  label: string;
  value: string;
  setValue: (value: string) => void;
  visible: boolean;
  setVisible: (value: boolean) => void;
  autoComplete?: string;
}

function PasswordField({
  label,
  value,
  setValue,
  visible,
  setVisible,
  autoComplete,
}: PasswordFieldProps) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700">
        {label}

        <div className="relative mt-2">
          <input
            type={visible ? "text" : "password"}
            required
            value={value}
            onChange={(event) =>
              setValue(event.target.value)
            }
            autoComplete={autoComplete}
            className="h-12 w-full rounded-xl border border-slate-300 px-4 pr-12 text-sm font-normal outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
          />

          <button
            type="button"
            onClick={() => setVisible(!visible)}
            aria-label={
              visible ? "Hide password" : "Show password"
            }
            aria-pressed={visible}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
          >
            {visible ? (
              <EyeOff
                aria-hidden="true"
                className="h-5 w-5"
              />
            ) : (
              <Eye
                aria-hidden="true"
                className="h-5 w-5"
              />
            )}
          </button>
        </div>
      </label>
    </div>
  );
}